import React, { useState, useEffect, useCallback } from 'react';
import { FileText, RefreshCw, ChevronLeft, ChevronRight, Sparkles, Target, Tag, Lightbulb, MessageSquare, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import ReportCard from './ReportCard';
import * as fbMonitorApi from '../../api/fbMonitor';

const REPORTS_PER_PAGE = 10;

const ReportsView = () => {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  // Expandable sections state
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    hotLeads: true,
    topics: false,
    actions: false,
    rawPosts: false,
  });

  const loadReports = useCallback(async (pageNum = 0) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fbMonitorApi.getReports({
        limit: REPORTS_PER_PAGE,
        offset: pageNum * REPORTS_PER_PAGE,
      });
      setReports(data.reports || []);
      setHasMore(data.has_more || false);
      setTotal(data.total || 0);
      setPage(pageNum);

      // Auto-select first report if none selected
      if (!selectedReport && data.reports?.length > 0) {
        setSelectedReport(data.reports[0]);
      }
    } catch (err) {
      console.error('Failed to load reports:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedReport]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading && reports.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-sky-500 animate-spin" />
          <p className="text-slate-400">Loading reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
          Failed to Load Reports
        </h2>
        <p className="dark:text-slate-400 text-slate-500 mb-4">{error}</p>
        <button
          onClick={() => loadReports()}
          className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-semibold"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h2 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
          No Reports Yet
        </h2>
        <p className="dark:text-slate-400 text-slate-500 max-w-md mx-auto">
          Reports are generated daily based on your monitored sources. Check back tomorrow for your first report.
        </p>
      </div>
    );
  }

  return (
    <div className="flex gap-6 h-full">
      {/* Reports List */}
      <div className="w-80 flex-shrink-0 space-y-3">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold dark:text-white text-slate-900">
            Reports ({total})
          </h2>
          <button
            onClick={() => loadReports(page)}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Report Cards */}
        <div className="space-y-2">
          {reports.map((report) => (
            <ReportCard
              key={report.id || report.date}
              report={report}
              onClick={setSelectedReport}
              isSelected={selectedReport?.id === report.id || selectedReport?.date === report.date}
            />
          ))}
        </div>

        {/* Pagination */}
        {(page > 0 || hasMore) && (
          <div className="flex items-center justify-between pt-4">
            <button
              onClick={() => loadReports(page - 1)}
              disabled={page === 0 || isLoading}
              className="p-2 dark:text-slate-400 text-slate-500 hover:dark:text-white hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm dark:text-slate-400 text-slate-500">
              Page {page + 1}
            </span>
            <button
              onClick={() => loadReports(page + 1)}
              disabled={!hasMore || isLoading}
              className="p-2 dark:text-slate-400 text-slate-500 hover:dark:text-white hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Report Detail */}
      <div className="flex-1 overflow-auto">
        {selectedReport ? (
          <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl">
            {/* Header */}
            <div className="p-6 border-b dark:border-slate-800 border-slate-200">
              <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-1">
                Daily Lead Report
              </h2>
              <p className="dark:text-slate-400 text-slate-500">
                {formatDate(selectedReport.date || selectedReport.created_at)}
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Executive Summary */}
              <CollapsibleSection
                title="Executive Summary"
                icon={Sparkles}
                isExpanded={expandedSections.summary}
                onToggle={() => toggleSection('summary')}
              >
                <p className="dark:text-slate-300 text-slate-600 whitespace-pre-wrap">
                  {selectedReport.executive_summary || 'No summary available.'}
                </p>
              </CollapsibleSection>

              {/* Hot Leads */}
              {selectedReport.hot_leads?.length > 0 && (
                <CollapsibleSection
                  title={`Hot Leads (${selectedReport.hot_leads.length})`}
                  icon={Target}
                  isExpanded={expandedSections.hotLeads}
                  onToggle={() => toggleSection('hotLeads')}
                  variant="success"
                >
                  <div className="space-y-3">
                    {selectedReport.hot_leads.map((lead, idx) => (
                      <div
                        key={idx}
                        className="p-4 dark:bg-green-500/5 bg-green-50 rounded-lg border dark:border-green-500/20 border-green-200"
                      >
                        <p className="dark:text-green-300 text-green-700 font-medium mb-2">
                          {lead.title || `Lead ${idx + 1}`}
                        </p>
                        <p className="text-sm dark:text-green-400/80 text-green-600">
                          {lead.description || lead.content}
                        </p>
                        {lead.source && (
                          <p className="text-xs dark:text-green-500/60 text-green-500 mt-2">
                            Source: {lead.source}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}

              {/* Key Topics */}
              {selectedReport.key_topics?.length > 0 && (
                <CollapsibleSection
                  title="Key Topics"
                  icon={Tag}
                  isExpanded={expandedSections.topics}
                  onToggle={() => toggleSection('topics')}
                >
                  <div className="flex flex-wrap gap-2">
                    {selectedReport.key_topics.map((topic, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 dark:bg-slate-800 bg-slate-100 dark:text-slate-300 text-slate-600 rounded-full text-sm"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </CollapsibleSection>
              )}

              {/* Recommended Actions */}
              {selectedReport.recommended_actions?.length > 0 && (
                <CollapsibleSection
                  title="Recommended Actions"
                  icon={Lightbulb}
                  isExpanded={expandedSections.actions}
                  onToggle={() => toggleSection('actions')}
                >
                  <ul className="space-y-2">
                    {selectedReport.recommended_actions.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        <span className="dark:text-slate-300 text-slate-600">{action}</span>
                      </li>
                    ))}
                  </ul>
                </CollapsibleSection>
              )}

              {/* Raw Posts */}
              {selectedReport.raw_posts?.length > 0 && (
                <CollapsibleSection
                  title={`All Posts (${selectedReport.raw_posts.length})`}
                  icon={MessageSquare}
                  isExpanded={expandedSections.rawPosts}
                  onToggle={() => toggleSection('rawPosts')}
                >
                  <div className="space-y-3 max-h-96 overflow-auto">
                    {selectedReport.raw_posts.map((post, idx) => (
                      <div
                        key={idx}
                        className="p-3 dark:bg-slate-800 bg-slate-50 rounded-lg"
                      >
                        <p className="text-sm dark:text-slate-300 text-slate-600 whitespace-pre-wrap">
                          {post.content || post.text}
                        </p>
                        {post.source && (
                          <p className="text-xs dark:text-slate-500 text-slate-400 mt-2">
                            From: {post.source}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="dark:text-slate-400 text-slate-500">
              Select a report to view details
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, isExpanded, onToggle, variant = 'default' }) => {
  const variants = {
    default: 'dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-700 border-slate-200',
    success: 'dark:bg-green-500/5 bg-green-50 border dark:border-green-500/20 border-green-200',
  };

  const iconVariants = {
    default: 'text-sky-500',
    success: 'text-green-500',
  };

  return (
    <div className={`rounded-xl overflow-hidden ${variants[variant]}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 hover:bg-slate-100/50 dark:hover:bg-slate-700/30 transition-colors"
      >
        <Icon className={`w-5 h-5 ${iconVariants[variant]}`} />
        <span className="font-medium dark:text-white text-slate-900 flex-1 text-left">
          {title}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 dark:text-slate-400 text-slate-500" />
        ) : (
          <ChevronDown className="w-5 h-5 dark:text-slate-400 text-slate-500" />
        )}
      </button>
      {isExpanded && (
        <div className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );
};

export default ReportsView;
